import { useState, useEffect } from "react";
import clinicianSignUp from "../assets/images/clinicianSignUp.png";
import patientSignUp from "../assets/images/patientSignUp.png";
import { useNavigate } from "react-router-dom";

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    role: "",
    firstName: "",
    lastName: "",
    license: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState({});

  // ✅ Handle Input Changes & Clear Errors
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormError((prevErrors) => ({ ...prevErrors, [name]: "" })); // Clear error when user types
  };

  // ✅ Validation Function
  const validateForm = () => {
    let errors = {};

    // First Name: Must be at least 3 characters
    if (formData.firstName.length < 3) {
      errors.firstName = "First name must be at least 3 characters";
    }

    // License Number (Clinician only): Must follow a basic pattern
    if (
      formData.role === "Clinician" &&
      !/^[A-Za-z]{2}\d{4,8}$/.test(formData.license)
    ) {
      errors.license = "Invalid license number format (e.g., MD123456)";
    }

    // Email: Must follow a valid email format
    if (!/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    // Password: Must be at least 8 characters
    if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    // Confirm Password: Must match Password
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ SignUp Handler (Efficient & Clean)
  const handleSignUp = async () => {
    if (!validateForm()) return;

    try {
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          ...(formData.role === "Clinician" && { license: formData.license }),
        }),
        redirect: "follow",
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`,
        requestOptions
      );
      let data = await response.json();

      console.log("Response status:", response.ok);
      if (response.ok && data.user) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("role", data.user.role);
        localStorage.setItem("user", JSON.stringify(data.user));

        if (data.user.role === "Patient") {
          if (data.user.profile) {
            const res = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/patients/id/${data.user.profile}`,
              {
                headers: {
                  Authorization: `Bearer ${data.token}`,
                },
              }
            );
            const patientData = await res.json();
            return navigate(`/patients/${patientData.uid}`);
          } else {
            return navigate("/patient-not-registered");
          }
        }
        navigate("/patients");
      } else {
        if (
          data.message === "Patient already exists" ||
          data.message === "Email already exists"
        ) {
          setFormError((prevErrors) => ({
            ...prevErrors,
            email: "Email already exists. Please log in.",
          }));
        } else {
          console.error(
            "Error:",
            data.message || "Unexpected response format",
            data
          );
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
    }
  };

  return (
    <div className="signup">
      {formData.role === "" ? (
        <div
          className="col centerAlign"
          style={{ margin: "auto", width: "60%" }}
        >
          <div className="h1 hippiBlue">USER SELECTION</div>
          <div className="b2 textGrey textCenter mobileHide">
            Please select your role: Patient or Clinician. This helps us
            personalize your experience.
          </div>
          <div className="row" style={{ gap: "4rem", padding: "2rem 0" }}>
            <div className="col centerAlign">
              <div className="width100">
                <img src={clinicianSignUp} alt="clinicianSignUp" />
              </div>
              <button
                className="button1 h3"
                name="role"
                value="Clinician"
                onClick={handleChange}
              >
                CLINICIAN SIGNUP
              </button>
            </div>
            <div className="col centerAlign">
              <div className="width100">
                <img src={patientSignUp} alt="patientSignUp" />
              </div>
              <button
                className="button1 h3"
                name="role"
                value="Patient"
                onClick={handleChange}
              >
                PATIENT SIGNUP
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="col centerAlign"
          style={{ margin: "auto", width: "60%" }}
        >
          <div className="h1 hippiBlue">
            {formData.role === "Patient"
              ? "PATIENT SIGNUP"
              : "CLINICIAN SIGNUP"}
          </div>
          <div
            className="col"
            style={{ padding: "2rem 0" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSignUp();
              }
            }}
          >
            <input
              type="text"
              placeholder="First Name"
              name="firstName"
              onChange={handleChange}
            />
            {formError.firstName && (
              <div className="b3 error">*{formError.firstName}</div>
            )}

            <input
              type="text"
              placeholder="Last Name"
              name="lastName"
              onChange={handleChange}
            />

            {formData.role === "Clinician" && (
              <>
                <input
                  type="text"
                  name="license"
                  placeholder="License/Registration Number"
                  onChange={handleChange}
                />
                {formError.license && (
                  <div className="b3 error">*{formError.license}</div>
                )}
              </>
            )}

            <input
              type="text"
              placeholder="Email"
              name="email"
              onChange={handleChange}
            />
            {formError.email && (
              <div className="b3 error">*{formError.email}</div>
            )}

            <input
              type="password"
              placeholder="Password"
              name="password"
              onChange={handleChange}
            />
            {formError.password && (
              <div className="b3 error">*{formError.password}</div>
            )}

            <input
              type="password"
              placeholder="Confirm Password"
              name="confirmPassword"
              onChange={handleChange}
            />
            {formError.confirmPassword && (
              <div className="b3 error">*{formError.confirmPassword}</div>
            )}

            <button className="button1 h3" onClick={handleSignUp}>
              SIGNUP
            </button>
            <div className="b3 textCenter">
              Already a member?{" "}
              <a href="/signin" className="hippiBlue b4">
                Login Now
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;
